import { useEffect, useState } from "react";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { kindIcon } from "@/pages/coach/methods/shared";
import { Music, Video } from "lucide-react";

export function MethodPreviewDialog({ method, onClose }) {
  const t = useT();
  const total = method?.preview_seconds || 15;
  const [remaining, setRemaining] = useState(total);

  useEffect(() => {
    if (!method) return;
    setRemaining(total);
    const iv = setInterval(() => setRemaining((s) => (s <= 1 ? (clearInterval(iv), 0) : s - 1)), 1000);
    return () => clearInterval(iv);
  }, [method, total]);

  if (!method) return null;
  const Icon = kindIcon(method.kind);
  const pct = total > 0 ? Math.max(0, Math.min(100, (1 - remaining / total) * 100)) : 100;

  return (
    <Dialog open={!!method} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="cm-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Icon className="h-5 w-5 text-primary" /> {method.title}</DialogTitle>
          <DialogDescription>{t("cm.preview_lbl")} · {t(`cm.kind_${method.kind}`)}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-3 py-2">
          <p className="font-display text-5xl font-semibold text-primary">{remaining}s</p>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary">
            <div className="h-full rounded-full bg-primary transition-[width] duration-1000 ease-linear" style={{ width: `${pct}%` }} />
          </div>
          <p className="text-center text-xs text-muted-foreground">{remaining > 0 ? t("cm.preview_running") : t("cm.preview_done")}</p>
        </div>
        {method.audio_url ? (
          <div className="flex items-center gap-2 rounded-xl border border-border bg-secondary p-3 text-sm text-muted-foreground"><Music className="h-4 w-4 text-primary" /> <span className="truncate">{method.audio_url}</span></div>
        ) : method.video_url ? (
          <div className="flex items-center gap-2 rounded-xl border border-border bg-secondary p-3 text-sm text-muted-foreground"><Video className="h-4 w-4 text-primary" /> <span className="truncate">{method.video_url}</span></div>
        ) : method.description ? (
          <p className="line-clamp-4 rounded-xl bg-secondary p-3 text-sm text-muted-foreground">{method.description}</p>
        ) : null}
        <Button className="w-full rounded-full" onClick={onClose} data-testid="cm-preview-close">{t("common.close")}</Button>
      </DialogContent>
    </Dialog>
  );
}
